import { useMemo } from 'react';
import Link from '@/components/compat/Link';
import { ArrowRight } from 'lucide-react';
import {
  type AceInfo,
  getRelatedAces,
  getRelatedAcesTitle,
} from '@/lib/aces-data';
import { useCatalogAces } from '@/components/painel/aces/aces-cms-context';
import { GsImage, XatsPriceValue } from '@/components/help-center/latest-power-shared';
import PowerDarkCardShell from '@/components/help-center/powers/PowerDarkCardShell';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';
import { formatAceDisplayName } from '@/components/help-center/aces/ace-shared';
import { stripHtmlToText } from '@/lib/cms-rich-helpers';

const ACE_BADGE_STYLE = {
  backgroundColor: 'rgba(245, 158, 11, 0.14)',
  borderColor: 'rgba(245, 158, 11, 0.45)',
  color: '#fbbf24',
  boxShadow: '0 0 12px rgba(245, 158, 11, 0.18)',
} as const;

function RelatedAceCard({ ace }: { ace: AceInfo }) {
  return (
    <Link href={`/aces/${ace.slug}`} className="group block">
      <PowerDarkCardShell
        className="h-full transition-all duration-300 hover:border-amber-400/40 hover:ring-amber-400/30 hover:-translate-y-1 hover:shadow-blue-950/50"
        innerClassName="flex items-center gap-4 p-4"
      >
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
          style={{
            background:
              'radial-gradient(circle at 50% 48%, #3a2a0a 0%, #1a1206 42%, #0c0904 75%, #030719 100%)',
            boxShadow: '0 0 0 1.5px #4a3510, 0 0 0 2.5px #6b4e18',
          }}
        >
          <GsImage
            code={ace.smileyCode || ace.slug}
            className="flex h-full w-full items-center justify-center"
            imgClassName="max-h-[44px] max-w-[44px] object-contain"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="mb-0.5 flex items-center gap-2">
            <h4 className="truncate font-poppins text-sm font-bold text-white transition-colors group-hover:text-amber-100">
              {formatAceDisplayName(ace.slug)}
            </h4>
            <span
              className="hidden shrink-0 items-center rounded-full border px-2 py-0.5 font-poppins text-[9px] font-bold uppercase tracking-wide sm:inline-flex"
              style={ACE_BADGE_STYLE}
            >
              Ace
            </span>
          </div>
          <p className="truncate font-inter text-[11px] leading-snug text-blue-100/70 line-clamp-1">
            {stripHtmlToText(ace.shortDescription)}
          </p>
          <XatsPriceValue className="mt-0.5 text-[11px] text-white">
            {ace.storePriceXats} xats / {ace.subscriptionDays}d
          </XatsPriceValue>
        </div>

        <ArrowRight className="h-4 w-4 shrink-0 text-blue-100/30 transition-all group-hover:translate-x-0.5 group-hover:text-amber-400" />
      </PowerDarkCardShell>
    </Link>
  );
}

export function AceDetailRelated({ ace }: { ace: AceInfo }) {
  const sectionTitle = getRelatedAcesTitle(ace);
  const { aces: catalogAces } = useCatalogAces();

  const related = useMemo(
    () => getRelatedAces(ace, 4, catalogAces),
    [ace, catalogAces]
  );

  if (related.length === 0) return null;

  return (
    <section className="border-t border-gray-100 bg-gray-50 py-14">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div className="flex items-center">
            <PowerSectionSmileyIcon code="yellow" size="xl" />
            <div className="-ml-1">
              <span
                className="mb-1.5 inline-flex items-center rounded-full border px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest"
                style={ACE_BADGE_STYLE}
              >
                Aces
              </span>
              <h2 className="font-poppins text-xl font-bold text-gray-900">{sectionTitle}</h2>
            </div>
          </div>
          <Link
            href="/aces"
            className="inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-4 py-2.5 font-poppins text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-amber-200 hover:bg-amber-50 hover:text-amber-800 hover:shadow-md sm:self-auto"
          >
            Ver todos
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((item) => (
            <RelatedAceCard key={item.slug} ace={item} />
          ))}
        </div>
      </div>
    </section>
  );
}
